import { create } from "zustand";
import { StateStorage, createJSONStorage, persist } from "zustand/middleware";
import EncryptedStorage from "../../../services/localStorage/encryptedStorage/EncryptedStorage";
import { IDevice } from "../interface/Device";
import { OperationMode } from '../presentation/context/OperationMode';
import { GetOperationModeAction, IsSystemTypeAction } from "./actions/SetOperationMode";
import { SystemType } from "../domain/enums/SystemType";
import { IDeviceProfile } from "../interface/DeviceProfile";
import DeviceService from "../../../services/api/device/DeviceService";
import GetDeviceUseCase from "../application/GetDeviceUseCase";
import GetDeviceProfileByDeviceIdUseCase from "../application/GetDeviceConfigUseCase";
import { IHub } from "../../hub/domain/IHub";
import HubService from "../../../services/api/hub/HubService";
import GetHubKeyByLocalIdUseCase from "../../hub/application/useCases/GetHubKeyByLocalIdUseCase";
import { GetPrinterSectorUseCase } from "../../hub/application/useCases/GetPrinterSectorUseCase";
import { IPrinterSector } from "../../hub/domain/IPrinterSector";
import ISimplePos from "../../simplePos/model/ISimplePos";
import GetSimplePosByDeviceUseCase from "../../simplePos/application/useCases/GetSimplePosByDeviceUseCase";
import SimplePosService from "../../../services/api/simplePos/SimplePosService";
import { ISession } from "../../session/domain/ISession";
import GetCurrencyUseCase from "../application/GetCurrencyUseCase";


export interface IDeviceStore {
    device?: IDevice;
    deviceConfig?: IDeviceProfile;
    isLoadingDevice: boolean;
    setDevice: (device?: IDevice) => void;
    setDeviceConfig: (deviceConfig?: IDeviceProfile, session?: ISession) => void;
    operationMode?: OperationMode;
    isSystemType: (systemType: SystemType) => boolean;
    disbledSound: boolean;
    setDisabledSound: (disabled: boolean) => void;
    getDevice: (deviceId: string) => Promise<IDevice>;
    getDeviceConfig: (device: IDevice, session?: ISession) => Promise<IDeviceProfile>;
    isLoadingDeviceConfig: boolean;
    clearDevice: () => void;
    hub?: IHub;
    getHub: (localId: string) => Promise<IHub>;
    receiptPrinterSector?: IPrinterSector;
    getReceiptPrinterSector: (sectorId: string) => Promise<IPrinterSector | undefined>;
    simplePos?: ISimplePos[];
    getSimplePos: (localId: string, deviceId: string) => Promise<ISimplePos[]>;
    currency?: { code: string; symbol: string };
    getCurrency: (device: IDevice) => Promise<void>
}


//const UseDeviceStore = create<IDeviceStore>()(persist(
const UseDeviceStore = create<IDeviceStore>()(
    persist((set, get) => ({
        device: undefined,
        deviceConfig: undefined,
        setDevice: (device?: IDevice) => set(store => ({ device })),
        setDeviceConfig: (deviceConfig?: IDeviceProfile, session?: ISession) => set(store => ({ deviceConfig, operationMode: GetOperationModeAction(deviceConfig, session) })),
        operationMode: undefined,
        isSystemType: (systemType) => {
            const { deviceConfig } = get();
            return !!(deviceConfig && IsSystemTypeAction(deviceConfig, systemType))
        },
        disbledSound: true,
        isLoadingDeviceConfig: false,
        isLoadingDevice: false,
        setDisabledSound: (disabled: boolean) => set(store => ({ disbledSound: disabled })),
        getDevice: async (deviceId: string) => {
            try {
                set({ isLoadingDevice: true });
                const deviceService = DeviceService();
                const response = await GetDeviceUseCase(deviceService, deviceId);
                set({ device: response });
                return response;
            } finally {
                set({ isLoadingDevice: false });
            }
        },
        getDeviceConfig: async (device: IDevice, session?: ISession) => {
            try {
                set({ isLoadingDeviceConfig: true });
                const deviceApi = DeviceService();
                const profile = await GetDeviceProfileByDeviceIdUseCase(deviceApi, device);
                get().setDeviceConfig(profile, session);
                return profile;
            } finally {
                set({ isLoadingDeviceConfig: false });
            }
        },
        clearDevice: () => {
            set({ device: undefined, deviceConfig: undefined });
        },
        hubKey: undefined,
        getHub: async (localId: string) => {
            const service = HubService();
            const hub = await GetHubKeyByLocalIdUseCase(service, localId);
            set({ hub });
            return hub;
        },
        receiptPrinterSector: undefined,
        getReceiptPrinterSector: async (sectorId: string) => {
            const service = HubService();
            try {
                const sector = await GetPrinterSectorUseCase(service, sectorId);
                set({ receiptPrinterSector: sector });
                return sector;
            } catch (error) {
                set({ receiptPrinterSector: undefined });
                return undefined;
            }
        },
        simplePos: undefined,
        getSimplePos: async (localId: string, deviceId: string) => {
            const service = SimplePosService();
            const simplePos = await GetSimplePosByDeviceUseCase(service, localId, deviceId);
            set({ simplePos });
            return simplePos;
        },
        currency: JSON.parse(localStorage.getItem(`@currency`) || '{}') as { code: string; symbol: string; },
        getCurrency: async (device: IDevice) => {
            try {
                const deviceApi = DeviceService();
                const currency = await GetCurrencyUseCase(deviceApi, device);
                localStorage.setItem(`@currency`, JSON.stringify({ code: currency?.code ?? "BRL", symbol: currency?.symbol ?? "R$" }));
            } catch {
                localStorage.setItem(`@currency`, JSON.stringify({ code: "BRL", symbol: "R$" }));
            } finally {
                set({ isLoadingDeviceConfig: false });
            }
        },
    }),
   {
       name: "device",
       storage: createJSONStorage<StateStorage>(EncryptedStorage),
   }
));

export default UseDeviceStore;